import { useEffect, useMemo } from 'react';
import { RequestBooking, RequestBookingQueryFilterMGT } from '../../../shared/types';
import { chunk, isEmpty } from 'lodash';
import { usePageState } from '../../../Hooks/usePageState';
import { useOverviewLinkState } from '../linkstate/requestBookingOverview.linkstate';
import { useGetRequestBookingsQuery } from '../../../GraphQL/hooks/useGetRequestBookingsQuery';
import { useUserState } from '../../../Hooks/useUserState';

const pageSize = 10;
const getPageCount = (RequestBookings: RequestBooking[]) => Math.ceil(RequestBookings?.length / pageSize);
const getCurrentPage = (requestedPage: number, RequestBookings: RequestBooking[]) => {
    const pageCount = getPageCount(RequestBookings);
    return requestedPage < 1 ? 1 : requestedPage > pageCount ? pageCount : requestedPage;
};

export const useSortedRequestBookingQuery = () => {
    const { setLoading } = usePageState();
    const [state, setState] = useOverviewLinkState();
    const { ...userState } = useUserState();
    const { page, search, selectedRequestBookingStatus } = state;

    const request = useMemo(
        () =>
            ({
                search: search ?? '',
                requestBookingStatusTypes: isEmpty(selectedRequestBookingStatus)
                    ? undefined
                    : selectedRequestBookingStatus,
                operatorId: userState.operatorId,
            }) as RequestBookingQueryFilterMGT,
        [search, selectedRequestBookingStatus]
    );

    const { data: unsortedRequestBookings, refetch, loading } = useGetRequestBookingsQuery(request);
    useEffect(() => setLoading(loading), [loading]);

    const setPage = (newPage: number) => {
        const pageCount = getPageCount(unsortedRequestBookings);
        // console.log('new page', newPage, pageCount);
        const page = newPage < 1 ? 1 : newPage > pageCount ? pageCount : newPage;
        setState({ ...state, page });
    };

    const { pageCount, totalCount, sortedRequestBookings } = useMemo(() => {
        const totalCount = unsortedRequestBookings?.length ?? 0;
        const pageCount = getPageCount(unsortedRequestBookings);
        const sortedRequestBookings = chunk(unsortedRequestBookings, pageSize);

        return { totalCount, pageCount, sortedRequestBookings };
    }, [unsortedRequestBookings]);

    const currentPage = getCurrentPage(page, unsortedRequestBookings);

    return {
        sortedRequestBookings: sortedRequestBookings?.length > 0 ? sortedRequestBookings[currentPage - 1] : [],
        refetch: () => refetch({ request }),
        page: page ?? 1,
        pageCount,
        totalCount,
        pageSize,
        setPage,
        loading,
    };
};
